import { deburr, toLower, trim } from 'lodash/fp';
import { flow } from 'fp-ts/function';
import { Labeled } from '@optimus-web/core';
import { DecimalService } from '@mews/framework';

/**
 * Represents prices with formatted price as the key and parsed number as the value.
 * @example
 * { "€0.22": 0.22 }
 */
export type Prices = Record<string, number>;

const normalize = flow(deburr, toLower, trim);

interface FindCountriesOptions {
    primaryText: string;
    secondaryText: string;
    searchValue: string;
    prices: Prices;
}

export const findCountries = ({ primaryText, secondaryText, searchValue, prices }: FindCountriesOptions) => {
    const searchNumber = parseFloat(searchValue);
    const countryPrice = prices[secondaryText];

    if (countryPrice == null || Number.isNaN(searchNumber)) {
        const normalizedPrimaryText = normalize(primaryText);
        const normalizedSecondaryText = normalize(secondaryText);

        return [normalizedPrimaryText, normalizedSecondaryText].join(' ').trim().includes(searchValue);
    }

    const searchPriceDecimal = DecimalService.parse(searchNumber);
    const itemPriceDecimal = DecimalService.parse(countryPrice);

    return DecimalService.isGreaterOrEqual(itemPriceDecimal, searchPriceDecimal);
};

export const findCountriesFromSelect =
    ({ prices }: { prices: Prices }) =>
    (component: React.ReactElement<Labeled>, searchValue: string) => {
        const { primaryText, secondaryText } = component.props;
        const primaryTextString = typeof primaryText === 'string' ? primaryText : '';
        const secondaryTextString = typeof secondaryText === 'string' ? secondaryText : '';

        return findCountries({
            prices,
            primaryText: primaryTextString,
            searchValue,
            secondaryText: secondaryTextString,
        });
    };
